import { defineStore } from "pinia";

function calcGain(close?: number, prior?: number): number | null {
  if (typeof close !== "number" || typeof prior !== "number" || prior === 0) {
    return null;
  }

  return ((close - prior) / prior) * 100;
}

/* =========================
   Types
========================= */

export type SnapshotRow = {
  symbol: string;
  trade_date: string;

  // price / momentum
  gain: number;
  prior: number;
  close: number;
  average: number;
  high:number;
  low:number;
  open:number;

  // liquidity / size
  total_value: number;
  tr_value:number;
  market_cap: number;

  // valuation
  pe: number;
  pbv: number;
  bvps: number | null;
  dividend_yield: number | null;
};

type SnapshotNormalizedRaw = {
  symbol: string;
  trade_date: string;

  gain?: number;
  prior?: number;
  close?: number;
  average?: number;
  high:number;
  low:number;
  open:number;

  total_value?: number;
  tr_value:number;
  market_cap?: number;

  pe?: number;
  pbv?: number;
  bvps?: number | null;
  dividend_yield?: number | null;
};

/* =========================
   Type Guard (TOLERANT)
========================= */

function isSnapshotNormalized(v: any): v is SnapshotNormalizedRaw {
  return (
    v &&
    typeof v === "object" &&
    typeof v.symbol === "string" &&
    typeof v.trade_date === "string"
  );
}

/* =========================
   Store
========================= */

export const useThaiStockSnapshotStore = defineStore("thaistockSnapshot", {
  state: () => ({
    loading: false,
    error: null as string | null,
    snapshotAt: null as string | null,

    // ✅ ใช้ Map จริง ๆ
    map: new Map<string, SnapshotRow>(),
  }),

  getters: {
    hasSnapshot(state): boolean {
      return state.map.size > 0;
    },

    getBySymbol:
      (state) =>
      (symbol: string): SnapshotRow | undefined => {
        return state.map.get(symbol);
      },

    // useful for debug / v-for
    asObject(state): Record<string, SnapshotRow> {
      return Object.fromEntries(state.map);
    },
  },

  actions: {
    /* =========================
         Fetch Snapshot
      ========================= */

    async fetchSnapshot(centerDate: string): Promise<void> {
      if (!centerDate) return;

      this.loading = true;
      this.error = null;

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 30000);

      try {
        const res = await fetch(
          "https://stock-api.nextbar.trade/internal/scrape/thaistock/snapshot",
          {
            method: "POST",
            signal: controller.signal,
          }
        );

        if (!res.ok) {
          throw new Error(`http ${res.status}`);
        }

        const json = await res.json();

        if (!json?.ok || !Array.isArray(json.normalized)) {
          throw new Error("invalid snapshot response");
        }

        // ✅ reset map ก่อน
        this.map.clear();

        for (const raw of json.normalized as SnapshotNormalizedRaw[]) {
          if (!isSnapshotNormalized(raw)) continue;

          // ❌ ไม่เอา snapshot ที่วันเดียวกับ EOD
          if (raw.trade_date === centerDate) continue;

          const gain = calcGain(raw.close, raw.prior);
          if (gain == null) continue;

          this.map.set(raw.symbol, {
            symbol: raw.symbol,
            trade_date: raw.trade_date,

            // ✅ computed gain (สำคัญ)
            gain,
            prior: Number(raw.prior),
            close: Number(raw.close),
            average: Number(raw.average ?? raw.close),
            high: Number(raw.high ?? raw.close),
            low: Number(raw.low ?? raw.close),
            open: Number(raw.open ?? raw.close),

            total_value: Number(raw.total_value ?? 0),
            tr_value: Number(raw.tr_value ?? 0),
            market_cap: Number(raw.market_cap ?? 0),

            pe: Number(raw.pe ?? 0),
            pbv: Number(raw.pbv ?? 0),
            bvps: raw.bvps ?? null,
            dividend_yield: raw.dividend_yield ?? null,
          });
        }

        this.snapshotAt = json.snapshot_date ?? new Date().toISOString();
      } catch (e: any) {
        if (e.name === "AbortError") {
          this.error = "snapshot timeout";
        } else {
          this.error = e.message ?? "snapshot error";
        }
      } finally {
        clearTimeout(timeout);
        this.loading = false;
      }
    },

    /* =========================
         Clear Snapshot
      ========================= */

    clear(): void {
      this.map.clear();
      this.snapshotAt = null;
      this.error = null;
    },
  },
});
